import { useTableActionContext } from '@aibees/elephant/context';
import { ACCORDION_IDS } from '@aibees/shared/store';
import { classnames } from '@aibees/shared/utils';
import { Avatar, Stack, Switch, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useIntegrationContext } from '../../context/integration-context';
import { configLabels, initialConfig } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type {
  IntegrationExportSettingItem,
  IntegrationExportSettingProps as Props,
} from './types';
import { useSettingsConfiguredText } from './use-settings-configured-text';

export const IntegrationExportSetting = memo(
  ({ className, expandedAccordion, setExpandedAccordion }: Props) => {
    const [config, setConfig] =
      useState<IntegrationExportSettingItem>(initialConfig);
    const { integrationConfig } = useTableActionContext();
    const { setIntegrationExportSetting } = useIntegrationContext();

    const handleChangeSwitch = (key: keyof IntegrationExportSettingItem) => {
      setConfig((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };

    const settingsConfiguredText = useSettingsConfiguredText(config);

    useEffect(() => {
      setIntegrationExportSetting(config);
    }, [config]);

    return (
      <Styled.Wrapper
        id={ACCORDION_IDS.INTEGRATION_EXPORT}
        className={classnames('IntegrationExportSetting', className)}
        sx={SX.root}
        avatar={
          <Avatar src={integrationConfig?.icon} size={22} variant="circular" />
        }
        title={`${integrationConfig?.name} Export Settings`}
        description={settingsConfiguredText || undefined}
        expandedAccordion={expandedAccordion}
        setExpandedAccordion={setExpandedAccordion}
      >
        <Stack gap={1}>
          {Object.entries(config).map(([key, value]) => (
            <Stack
              key={key}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body4" fontWeight={400} color="black">
                {configLabels[key as keyof IntegrationExportSettingItem]}
              </Typography>

              <Switch
                checked={value}
                onChange={() =>
                  handleChangeSwitch(key as keyof IntegrationExportSettingItem)
                }
                color="primary"
                size="small"
              />
            </Stack>
          ))}
        </Stack>
      </Styled.Wrapper>
    );
  }
);

IntegrationExportSetting.displayName = 'IntegrationExportSetting';
